// Pictures and documents pasted into the lesson composer become the
// lesson's attachments, as on iOS (ImagePasteEnabler.swift,
// CreateLessonSheet.swift `appendPastedImages` / `appendPastedFiles` /
// `appendAttachments`): text still pastes as text; a picture is attached
// as a JPEG at quality 0.8, like a photo just taken; a PDF, Word, Excel or
// PowerPoint file as itself. Five attachments at most, each one a type
// and a size Storage accepts (FileUploader.swift, server/storage.rules).

import { uuidV4 } from "./pairing.js";

/** LessonAttachmentModels.swift `maxLessonAttachments`. */
export const MAX_LESSON_ATTACHMENTS = 5;

/** FileUploader.noteMaxBytes, the limit storage.rules sets on note files. */
export const NOTE_MAX_BYTES = 25 * 1024 * 1024;

/** FileUploader.noteAllowedTypes. */
export const NOTE_ALLOWED_TYPES = new Set([
  "application/pdf",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/heic",
  "image/heif",
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/rtf",
]);

/** FileUploader.supportedExtensionsLabel. */
export const SUPPORTED_EXTENSIONS_LABEL = "PDF, Word, PowerPoint, Excel, JPG, PNG, HEIC, WEBP, GIF, TXT, MD, CSV, RTF";

/** JPEG quality of a pasted picture (`image.jpegData(compressionQuality: 0.8)`). */
export const PASTED_IMAGE_QUALITY = 0.8;

const MIME_BY_EXTENSION = {
  pdf: "application/pdf",
  gif: "image/gif",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt: "text/plain",
  md: "text/markdown",
  csv: "text/csv",
  rtf: "application/rtf",
};

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "bmp", "tif", "tiff"]);

// The documents a paste may attach: lessonSupportedDocumentTypes without
// its pictures (attached as JPEG) and its text types (pasted as text).
const PASTED_DOCUMENT_EXTENSIONS = new Set(["pdf", "doc", "docx", "ppt", "pptx", "xls", "xlsx"]);

/** Lower-case extension of a file name, "" when it has none. */
export function extensionOf(name) {
  const text = typeof name === "string" ? name : "";
  const dot = text.lastIndexOf(".");
  return dot > 0 && dot < text.length - 1 ? text.slice(dot + 1).toLowerCase() : "";
}

/** The type Storage is told: the one the name's extension implies, as iOS
 *  reads it, else the one the browser gave. */
export function mimeTypeOf(name, type) {
  const byName = MIME_BY_EXTENSION[extensionOf(name)];
  if (byName) return byName;
  return typeof type === "string" ? type.toLowerCase() : "";
}

function extensionForType(type) {
  const entry = Object.entries(MIME_BY_EXTENSION).find(([, mime]) => mime === type);
  return entry ? entry[0] : "";
}

/**
 * Why Storage would refuse this file, or null (FileUploader
 * `validationError`), as a message to show: `{ key, params }`, the key
 * under `newNote.attachments`.
 */
export function uploadValidationError(fileName, mimeType, byteCount) {
  const type = typeof mimeType === "string" ? mimeType.toLowerCase() : "";
  if (!NOTE_ALLOWED_TYPES.has(type)) {
    const ext = extensionOf(fileName);
    return { key: "unsupported", params: { ext: ext ? `.${ext}` : fileName, formats: SUPPORTED_EXTENSIONS_LABEL } };
  }
  if (byteCount > NOTE_MAX_BYTES) {
    return { key: "tooLarge", params: { name: fileName, limit: NOTE_MAX_BYTES / (1024 * 1024) } };
  }
  return null;
}

/** `max_files_selection_limit`. */
export const LIMIT_MESSAGE = Object.freeze({ key: "limit", params: { count: MAX_LESSON_ATTACHMENTS } });

function isImage(file) {
  const type = typeof file.type === "string" ? file.type.toLowerCase() : "";
  return type.startsWith("image/") || IMAGE_EXTENSIONS.has(extensionOf(file.name));
}

function isPastedDocument(file) {
  const ext = extensionOf(file.name) || extensionForType(mimeTypeOf("", file.type));
  return PASTED_DOCUMENT_EXTENSIONS.has(ext);
}

function clipboardFiles(data) {
  const files = Array.from((data && data.files) || []);
  if (files.length) return files;
  const out = [];
  for (const item of Array.from((data && data.items) || [])) {
    if (item.kind !== "file") continue;
    const file = item.getAsFile();
    if (file) out.push(file);
  }
  return out;
}

/** The last part of a path or `file://` address, decoded. */
function baseName(line) {
  let path = line.trim().replace(/^file:\/\/[^/]*/i, "");
  try {
    path = decodeURIComponent(path);
  } catch (_) {
    // Not percent-encoded after all: compared as written.
  }
  return path.slice(Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\")) + 1);
}

/** Text that only names the files that come with it: what a file manager
 *  puts next to files it copies (Finder writes their names), not words. */
function namesTheFiles(text, files) {
  const names = new Set(files.map((file) => file.name).filter(Boolean));
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  return names.size > 0 && lines.length > 0 && lines.every((line) => names.has(baseName(line)));
}

/**
 * What a paste into the composer attaches, `{ images, documents }`, or
 * null when it is a text paste: any text on the clipboard wins, the way a
 * web or Word selection that comes with a picture stays text on iOS —
 * unless that text is only the names of the files themselves. A drop on
 * the text (a `DataTransfer` too) follows the same rules, as on iOS.
 */
export function pastedAttachments(data) {
  if (!data) return null;
  const text = typeof data.getData === "function" ? data.getData("text/plain") : "";
  const files = clipboardFiles(data);
  if (text && !namesTheFiles(text, files)) return null;
  const images = files.filter(isImage);
  const documents = files.filter((file) => !isImage(file) && isPastedDocument(file));
  return images.length || documents.length ? { images, documents } : null;
}

/** Whether a drag carries files: they can be read only once dropped. */
export function dragCarriesFiles(data) {
  return !!data && Array.from(data.types || []).includes("Files");
}

/** How many of `wanted` new attachments fit after `count`, and the limit
 *  message when they do not all fit. */
export function attachmentSlots(count, wanted) {
  const free = Math.max(0, MAX_LESSON_ATTACHMENTS - count);
  return { take: Math.min(free, wanted), messages: wanted > free ? [LIMIT_MESSAGE] : [] };
}

/**
 * `appendAttachments`: the files Storage would refuse are left out (the
 * first one said), then as many as there are free slots. Resolves to
 * `{ accepted, messages }`, the messages in the order iOS shows them.
 */
export function admitAttachments(count, files) {
  const messages = [];
  let firstError = null;
  const valid = files.filter((file) => {
    const error = uploadValidationError(file.name, mimeTypeOf(file.name, file.type), file.size);
    if (error && !firstError) firstError = error;
    return !error;
  });
  if (firstError) messages.push(firstError);
  if (!valid.length) return { accepted: [], messages };
  const free = Math.max(0, MAX_LESSON_ATTACHMENTS - count);
  if (free === 0) return { accepted: [], messages: [...messages, LIMIT_MESSAGE] };
  if (valid.length > free) return { accepted: valid.slice(0, free), messages: [...messages, LIMIT_MESSAGE] };
  return { accepted: valid, messages };
}

/**
 * Pasted documents (`appendPastedFiles`): cut to the free slots first,
 * then admitted. Each keeps its name, with the extension its type implies
 * when the clipboard gave none, and a type Storage can check.
 */
export function admitPastedDocuments(count, documents) {
  const { take, messages } = attachmentSlots(count, documents.length);
  if (take === 0) return { accepted: [], messages: [LIMIT_MESSAGE] };
  const named = documents.slice(0, take).map(namedDocument);
  const admitted = admitAttachments(count, named);
  return { accepted: admitted.accepted, messages: [...messages, ...admitted.messages] };
}

function namedDocument(file) {
  const type = mimeTypeOf(file.name, file.type);
  let name = typeof file.name === "string" && file.name ? file.name : "document";
  if (!extensionOf(name)) {
    const ext = extensionForType(type);
    if (ext) name = `${name}.${ext}`;
  }
  if (name === file.name && type === file.type) return file;
  return new File([file], name, { type, lastModified: file.lastModified });
}

// ---------------------------------------------------------------------------
// In the browser only: the picture drawn into a canvas and taken as JPEG.

async function decodeImage(file) {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch (_) {
      // Some formats only an <img> decodes; try that below.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * A pasted picture as the JPEG attachment iOS makes of it
 * (`appendCapturedImage`): full size, quality 0.8, named `<UUID>.jpg`.
 * A transparent part turns white, as on the page it was copied from.
 * Resolves to null when the picture cannot be read.
 */
export async function pastedImageAsJpeg(file) {
  let image;
  try {
    image = await decodeImage(file);
  } catch (_) {
    return null;
  }
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  if (!width || !height) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);
  if (typeof image.close === "function") image.close();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", PASTED_IMAGE_QUALITY));
  if (!blob) return null;
  return new File([blob], `${uuidV4().toUpperCase()}.jpg`, { type: "image/jpeg" });
}
